import type { EventName, EventParams } from "./types";
import { callYm } from "./ym";

const IDEMPOTENCY_WINDOW_MS = 30_000;
const idempotency = new Map<string, number>();

function kopToFloat(kop: number): number {
  return kop / 100;
}

function enrichWithFloatAmounts<N extends EventName>(
  params: EventParams<N>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(params as Record<string, unknown>) };
  if (typeof out.revenue_kop === "number") out.revenue = kopToFloat(out.revenue_kop);
  if (typeof out.price_kop === "number") out.price = kopToFloat(out.price_kop);
  if (typeof out.amount_kop === "number") out.amount = kopToFloat(out.amount_kop);
  if (typeof out.subtotal_kop === "number") out.subtotal = kopToFloat(out.subtotal_kop);
  return out;
}

function isDuplicate(event: EventName, params: Record<string, unknown>): boolean {
  const orderId = typeof params.order_id === "string" ? params.order_id : null;
  if (orderId === null) return false;
  const key = `${event}::${orderId}`;
  const now = Date.now();
  const prev = idempotency.get(key);
  if (prev !== undefined && now - prev < IDEMPOTENCY_WINDOW_MS) return true;
  idempotency.set(key, now);
  return false;
}

export function trackEvent<N extends EventName>(name: N, params: EventParams<N>): void {
  const enriched = enrichWithFloatAmounts(params);
  if (isDuplicate(name, enriched)) return;
  callYm("reachGoal", name, enriched);
}

export function trackPageView(url: string): void {
  callYm("hit", url);
}

/**
 * 把当前 Metrica 访客和登录用户 id 关联(Yandex Metrica `setUserID`)。
 * 关联后,Metrica 里的浏览/停留/转化能归到具体登录用户(需求2:谁搜了什么/浏览/停留)。
 * 未登录(空 id)直接 no-op;counter 未 init 时 callYm 自身也会守住(counterId===0)。
 */
export function setUserId(userId: string): void {
  if (!userId) return;
  callYm("setUserID", userId);
}

/** Test-only */
export function resetIdempotencyForTests(): void {
  idempotency.clear();
}
